import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from 'app/spacing';
import { usePaginationSettings } from 'hooks/usePaginationSettings';
import { HistoryModal } from '__components/Modals/HistoryModal';
import { useColumns } from 'components/Modals/HistoryModal/hooks';
import { HistoryDetailsModal } from 'components/Modals/HistoryModal/HistoryDetailsModal';
import { serviceAccountHistoryQuery } from 'query/service-account';
import type { ServiceAccountTimelineEntry } from '@kizen/api/service-account';

type Props = {
  id: string;
  onHide(): void;
};

type ActionOption = {
  value: 'view';
};

const DEFAULT_PAGINATION_SETTINGS = {
  page: 1,
  pageSize: 10,
  ordering: '-created',
};

export const ServiceAccountHistoryModal = ({ id, onHide }: Props) => {
  const { t } = useTranslation();
  const [historyEntry, setHistoryEntry] =
    useState<ServiceAccountTimelineEntry | null>(null);
  const isMobile = useBreakpoint();
  const pagination = usePaginationSettings(DEFAULT_PAGINATION_SETTINGS);

  const { page, pageSize, ordering, search } = pagination;

  const historyQuery = useQuery(
    serviceAccountHistoryQuery(id, {
      params: { page, page_size: pageSize, ordering, search },
      placeholderData: keepPreviousData,
      staleTime: 0,
    })
  );

  const handleSelectAction = useCallback(
    ({ value }: ActionOption, data: ServiceAccountTimelineEntry) => {
      if (value === 'view') {
        setHistoryEntry(data);
      }
    },
    []
  );

  const handleHideHistoryDetails = useCallback(() => {
    setHistoryEntry(null);
  }, []);

  const columns = useColumns(handleSelectAction);

  if (isMobile) {
    return null;
  }

  return (
    <>
      <HistoryModal
        show
        showSearch
        heading={t('Service Account History Log')}
        onHide={onHide}
        onConfirm={onHide}
        loading={historyQuery.isLoading}
        results={historyQuery.data?.results ?? []}
        columns={columns}
        count={historyQuery.data?.count}
        pagination={pagination}
      />
      {historyEntry && (
        <HistoryDetailsModal
          data={historyEntry.data}
          onHide={handleHideHistoryDetails}
        />
      )}
    </>
  );
};
